/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type {
  mapTypes,
  layerTypes,
  syncGroupsTypes,
  CoreAppStore,
} from '@opengeoweb/store';
import { genericActions, syncGroupsSelectors } from '@opengeoweb/store';
import { ClockConnect } from '../ClockConnect';
import { ConfigurableMapConnect } from '../ConfigurableMapConnect';
import { LegendConnect } from '../LegendConnect';
import { TimeSliderConnect } from '../TimeSliderConnect';

const mapBoxStyle = (theme: Theme): SxProps<Theme> => ({
  boxSizing: 'border-box',
  float: 'left',
  '& .MapViewComponent': {
    border: `1px solid ${theme.palette.geowebColors.typographyAndIcons.iconLinkDisabled}`,
  },
});

export interface MultiMapPreset {
  id: string;
  title?: string;
  layers: layerTypes.Layer[];
  autoTimeStepLayerId?: string;
  autoUpdateLayerId?: string;
  bbox?: mapTypes.Bbox;
  srs?: string;
  dimensions?: mapTypes.Dimension[];
  displayTimeInMap?: boolean;
  displayLayerManagerAndLegendButtonInMap?: boolean;
  displayDimensionSelectButtonInMap?: boolean;
  syncGroupsIds?: string[];
  componentType?: string;
}
export interface MultiMapViewProps {
  rows: number;
  cols: number;
  maps: MultiMapPreset[];
  // eslint-disable-next-line react/no-unused-prop-types
  syncArea?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  syncTime?: boolean;
  showTimeSlider?: boolean;
  showZoomControls?: boolean;
  multiLegend?: boolean;
  isClockEnabled?: boolean;
}

export const MultiMapViewConnect: React.FC<MultiMapViewProps> = ({
  maps,
  rows,
  cols,
  showTimeSlider = true,
  showZoomControls = true,
  multiLegend = false,
  isClockEnabled = true,
}: MultiMapViewProps) => {
  const multiMapWindowRef = React.useRef(null);
  const dispatch = useDispatch();
  const syncGroupAddTarget = React.useCallback(
    (payload: syncGroupsTypes.SyncGroupAddTargetPayload): void => {
      dispatch(genericActions.syncGroupAddTarget(payload));
    },
    [dispatch],
  );
  const syncTargets = useSelector((store: CoreAppStore) =>
    syncGroupsSelectors.getSyncGroupTargets(store),
  );

  React.useEffect(() => {
    maps.forEach((map) => {
      map.syncGroupsIds?.forEach((syncGroupsId) => {
        const alreadySyncedTargetIndex = syncTargets.findIndex(
          (target) =>
            target.targetId === map.id &&
            target.groupId === syncGroupsId &&
            target.linked === true,
        );
        // Only trigger an action if the target was not yet added to the group or was not linked.
        if (alreadySyncedTargetIndex === -1) {
          syncGroupAddTarget({
            groupId: syncGroupsId,
            targetId: map.id,
            linked: true,
            origin: 'system',
          });
        }
      });
    });
  }, [maps, syncGroupAddTarget, syncTargets]);

  const mapStyle = {
    width: `${Math.round(100 / cols)}%`,
    height: `${Math.round(100 / rows)}%`,
  };
  if (maps.length === 0) {
    return null;
  }
  const firstMapId = maps[0].id;

  return (
    <div
      data-testid="MultiMapViewSliderConnect"
      ref={multiMapWindowRef}
      style={{ height: '100%', position: 'relative' }}
    >
      <div
        style={{
          position: 'absolute',
          left: '0px',
          bottom: '0px',
          zIndex: 1000,
          width: '100%',
        }}
      >
        {!!showTimeSlider && (
          <TimeSliderConnect
            isAlwaysVisible
            mapId={firstMapId}
            mapWindowRef={multiMapWindowRef}
            sourceId={firstMapId}
          />
        )}
      </div>
      <LegendConnect mapId={firstMapId} multiLegend={multiLegend} showMapId />
      {!!isClockEnabled && <ClockConnect mapId={firstMapId} />}
      {maps.map((map: MultiMapPreset, mapIndex) => {
        const mapId = map.id;
        return (
          <Box key={mapId} style={mapStyle} sx={mapBoxStyle as SxProps<Theme>}>
            <ConfigurableMapConnect
              autoTimeStepLayerId={map.autoTimeStepLayerId}
              autoUpdateLayerId={map.autoUpdateLayerId}
              dimensions={map.dimensions || []}
              disableTimeSlider
              displayDimensionSelectButtonInMap={
                map.displayDimensionSelectButtonInMap
              }
              displayLayerManagerAndLegendButtonInMap={
                map.displayLayerManagerAndLegendButtonInMap
              }
              displayTimeInMap={map.displayTimeInMap || false}
              id={mapId}
              initialBbox={map.bbox}
              isClockEnabled={false}
              layers={map.layers || []}
              multiLegend={multiLegend}
              shouldDisablePrefetching={map.componentType === 'MultiMap'}
              shouldShowZoomControls={!!showZoomControls && mapIndex === 0}
              showTimeSlider={false}
              srs={map.srs}
              title={map.title || mapId}
            />
          </Box>
        );
      })}
    </div>
  );
};
